// controllers/paymentController.js
import apiService from '../services/apiService';
import { authController } from './authController'; // 🆕

// Script dynamically load karne ka helper — same script dobara load nahi hoga
const loadScript = (src) => new Promise((resolve) => {
  if (document.querySelector(`script[src="${src}"]`)) return resolve(true);
  const script = document.createElement('script');
  script.src = src;
  script.onload  = () => resolve(true);
  script.onerror = () => resolve(false);
  document.body.appendChild(script);
});

const paymentController = {
  // Active gateways poochta hai backend se (GET /payment/gateways) —
  // jo bhi env mein configured hai (keys hain) sirf wahi dikhega UI mein
  getActiveGateways: async () => {
    try {
      const res = await apiService.get('payment/gateways');
      const all = res.data || [];
      return { success: true, data: all.filter(g => g.active) };
    } catch (e) {
      return { success: false, data: [], message: e.message || 'Failed to fetch gateways' };
    }
  },

  getMyInvoices: async () => {
    try {
      const res = await apiService.get('payments/invoices');
      return { success: true, data: res.invoices || [] };
    } catch (e) {
      return { success: false, data: [], message: e.message };
    }
  },
  // 🆕 Invoice PDF download — proper authenticated fetch + blob download,
  // taaki Bearer-token auth ho ya cookie-based, dono mein reliably kaam kare.
  // Sirf <a href="..."> use karna reliable nahi tha kyunki wo Authorization
  // header nahi bhej sakta agar backend Bearer token expect karta hai.
  downloadInvoice: async (invoiceId, invoiceNumber) => {
    try {
      // apiService jo bhi base URL use kar raha hai, wahi yahan bhi use karo
      const token = authController.getToken();
      const baseUrl = (apiService.defaults?.baseURL || apiService.baseURL || process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
      const url = `${baseUrl}/payments/invoices/${invoiceId}/download`;

      const res = await fetch(url, {
        method: 'GET',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include', // cookie-based auth ke liye bhi fallback
      });

      if (!res.ok) {
        let message = 'Failed to download invoice';
        try {
          const err = await res.json();
          message = err.message || message;
        } catch (e) { /* response JSON nahi tha, default message use karo */ }
        throw new Error(message);
      }

      const blob = await res.blob();
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `${invoiceNumber || invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);

      return { success: true };
    } catch (e) {
      return { success: false, message: e.message || 'Download failed' };
    }
  },

  // ───────────────────────────── RAZORPAY ─────────────────────────────
  razorpay: {
    // 🆕 FIX: planIds + billingCycle bhi bhejna zaroori hai — backend ka
    // razorpayController.js abhi inhi 2 fields se Invoice banata hai aur
    // amount SERVER SE compute karta hai (Plan model se), client ka bheja
    // 'amount' sirf fallback hai jab Plan model available na ho
    createOrder: async ({ amount, receipt, notes, planIds, billingCycle }) => {
      try {
        const res = await apiService.post('payment/razorpay/create-order', {
          amount, receipt, notes, planIds, billingCycle,
        });
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message || 'Order creation failed' }; }
    },
    verify: async (payload) => {
      try {
        const res = await apiService.post('payment/razorpay/verify', payload);
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message || 'Verification failed' }; }
    },
    // Checkout popup kholta hai — success/failure callbacks return karta hai
    checkout: async (orderData, user, onSuccess, onFailure) => {
      const loaded = await loadScript('https://checkout.razorpay.com/v1/checkout.js');
      if (!loaded) return onFailure('Razorpay SDK load nahi ho payi');

      const rzp = new window.Razorpay({
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        order_id: orderData.orderId,
        name: 'Tracklio Hub',
        description: 'Subscription Payment',
        prefill: {
          name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
          email: user?.email,
          contact: user?.phone,
        },
        handler: (response) => onSuccess(response),
        modal: { ondismiss: () => onFailure('Payment window band kar diya gaya') },
        theme: { color: '#3b82f6' },
      });
      rzp.on('payment.failed', (resp) => onFailure(resp.error?.description || 'Payment failed'));
      rzp.open();
    },
  },

  // ───────────────────────────── CASHFREE ─────────────────────────────
  cashfree: {
    // 🆕 FIX: planIds + billingCycle bhi bhejna zaroori hai — backend ka
    // cashfreeController.js bhi inhi se Invoice banata hai
    createOrder: async ({ amount, customer, meta, planIds, billingCycle }) => {
      try {
        const res = await apiService.post('payment/cashfree/create-order', {
          amount,
          customer,
          planIds,
          billingCycle,
          returnUrl: `${window.location.origin}/payment/cashfree/return?order_id={order_id}`,
          meta,
        });
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message || 'Order creation failed' }; }
    },
    fetchOrder: async (orderId) => {
      try {
        const res = await apiService.get(`payment/cashfree/order/${orderId}`);
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message }; }
    },
    checkout: async (orderData, onFailure) => {
      const loaded = await loadScript('https://sdk.cashfree.com/js/v3/cashfree.js');
      if (!loaded) { onFailure('Cashfree SDK load nahi ho payi'); return null; }
      const cashfree = window.Cashfree({ mode: orderData.mode || 'sandbox' });
      const result = await cashfree.checkout({
        paymentSessionId: orderData.paymentSessionId,
        redirectTarget: '_modal',
      });
      if (result?.error) onFailure(result.error.message);
      return result;
    },
  },

  // ───────────────────────────── PAYTM ─────────────────────────────
  // ⚠️ NOTE: Paytm/PhonePe backend controllers abhi tak Invoice model se
  // connect nahi kiye gaye (sirf razorpay + cashfree hue hain). Agar inhe
  // use karna hai to paytmController.js/phonepeController.js bhi bhejo —
  // main unme bhi wahi Invoice-creation pattern add kar dunga.
  paytm: {
    createOrder: async (amount, customerId, email, phone) => {
      try {
        const res = await apiService.post('payment/paytm/create-order', {
          amount, customerId, email, phone,
          callbackUrl: `${window.location.origin}/payment/paytm/callback`,
        });
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message || 'Order creation failed' }; }
    },
    verify: async (orderId) => {
      try {
        const res = await apiService.get(`payment/paytm/verify/${orderId}`);
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message }; }
    },
    // Paytm classic flow — form POST se naye tab mein checkout page kholta hai
    checkout: (orderData) => {
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = orderData.checkoutUrl;
      form.target = '_blank';
      Object.entries(orderData.params || {}).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type  = 'hidden';
        input.name  = key;
        input.value = value;
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    },
  },

  // ───────────────────────────── PHONEPE ─────────────────────────────
  phonepe: {
    createOrder: async (amount, customerId, mobileNumber) => {
      try {
        const res = await apiService.post('payment/phonepe/create-order', {
          amount, customerId, mobileNumber,
          redirectUrl: `${window.location.origin}/payment/phonepe/return`,
        });
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message || 'Order creation failed' }; }
    },
    checkStatus: async (txnId) => {
      try {
        const res = await apiService.get(`payment/phonepe/status/${txnId}`);
        return { success: true, data: res };
      } catch (e) { return { success: false, message: e.message }; }
    },
    checkout: (orderData) => {
      window.open(orderData.redirectUrl, '_blank');
    },
  },
};

export default paymentController;